<template>
  <div class="c-background-image-theme-options flex flex-col gap-3">
    <div v-if="!hideImageSelector" class="flex flex-col gap-2">
      <span class="inline-block leading-5 text-base font-normal" :style="{ color: themeVars.text }">
        {{ backgroundImagePlaceholder ?? t('canvasUI.components.CBackgroundImageThemeOptions.backgroundImage') }}
      </span>
      <CSingleFileSelector
        :file="
          imageUrl
            ? {
                thumbnail_url: imageUrl ?? '',
                content_type: FileContentTypeEnum.IMAGE,
              }
            : null
        "
        :hide-content-type="true"
        :selected-page-index="0"
        size="small"
        upload-Button-class="w-full"
        @clearSelectedFile="() => updateBackgroundProperty('backgroundImage', null)"
        @openContentSelector="emit('choose-content')"
      />
    </div>

    <template v-if="backgroundImage">
      <div v-if="!hideBackgroundSize" class="flex flex-col gap-2">
        <span class="inline-block leading-5 text-base font-normal" :style="{ color: themeVars.text }">
          {{ t('canvasUI.components.CBackgroundImageThemeOptions.size') }}
        </span>

        <CSelect
          bordered
          class="basis-1/2 select-none"
          :options="sizeOptions"
          :value="backgroundSize"
          @update:value="(v) => updateBackgroundProperty('backgroundSize', v)"
        />
      </div>

      <div v-if="showBackgroundPositionX" class="flex flex-col gap-2">
        <span class="inline-block leading-5 text-base font-normal" :style="{ color: themeVars.text }">
          {{ t('canvasUI.components.CBackgroundImageThemeOptions.positionHorizontal') }}
        </span>

        <CSelect
          bordered
          class="basis-1/2 select-none"
          :options="horizontalPositionOptions"
          :value="backgroundPositionX"
          @update:value="(v) => updateBackgroundProperty('backgroundPositionX', v)"
        />
      </div>

      <div v-if="showBackgroundPositionY" class="flex flex-col gap-2">
        <span class="inline-block leading-5 text-base font-normal" :style="{ color: themeVars.text }">
          {{ t('canvasUI.components.CBackgroundImageThemeOptions.positionVertical') }}
        </span>

        <CSelect
          bordered
          class="basis-1/2 select-none"
          :options="verticalPositionOptions"
          :value="backgroundPositionY"
          @update:value="(v) => updateBackgroundProperty('backgroundPositionY', v)"
        />
      </div>

      <div v-if="showBackgroundRepeat" class="flex flex-col gap-2">
        <span class="inline-block leading-5 text-base font-normal" :style="{ color: themeVars.text }">
          {{ t('canvasUI.components.CBackgroundImageThemeOptions.repeat') }}
        </span>

        <CSelect
          bordered
          class="basis-1/2 select-none"
          :options="repeatOptions"
          :value="backgroundRepeat"
          @update:value="(v) => updateBackgroundProperty('backgroundRepeat', v)"
        />
      </div>

      <div v-if="!hideBackgroundAttachment" class="flex flex-col gap-2">
        <span class="inline-block leading-5 text-base font-normal" :style="{ color: themeVars.text }">
          {{ t('canvasUI.components.CBackgroundImageThemeOptions.attachment') }}
        </span>

        <CSelect
          bordered
          class="basis-1/2 select-none"
          :options="attachmentOptions"
          :value="backgroundAttachment"
          @update:value="(v) => updateBackgroundProperty('backgroundAttachment', v)"
        />
      </div>
    </template>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'

import CSingleFileSelector from '@lib/components/CSingleFileSelector/CSingleFileSelector.vue'
import CSelect from '@lib/components/CSelect/CSelect.vue'

import { useI18n } from 'vue-i18n'
import useThemeVars from '@lib/composables/useThemeVars'

import { FileContentTypeEnum } from '@openapi'
import { extractUrlFromCssBackgroundImage } from '@canvas-builder/util/url'
import type {
  BackgroundPositionX,
  BackgroundPositionY,
  BackgroundRepeat,
  BackgroundSize,
  BackgroundAttachment,
} from '@lib/types/background'

const props = withDefaults(
  defineProps<{
    backgroundImage?: string
    backgroundImagePlaceholder?: string
    backgroundPositionX?: BackgroundPositionX
    backgroundPositionY?: BackgroundPositionY
    backgroundRepeat?: BackgroundRepeat
    backgroundSize?: BackgroundSize
    backgroundAttachment?: BackgroundAttachment
    backgroundSizeOptions?: BackgroundSize[]
    hideBackgroundAttachment?: boolean
    hideImageSelector?: boolean
    hideBackgroundSize?: boolean
    hideBackgroundPositionX?: boolean
    hideBackgroundPositionY?: boolean
    hideBackgroundRepeat?: boolean
  }>(),
  {
    hideBackgroundAttachment: false,
    hideImageSelector: false,
    hideBackgroundSize: false,
    hideBackgroundPositionX: false,
    hideBackgroundPositionY: false,
    hideBackgroundRepeat: false,
  },
)

const emit = defineEmits<{
  'choose-content': []
  'bg-property-change': [property: string, value: any]
}>()

const themeVars = useThemeVars()

const imageUrl = computed(() => extractUrlFromCssBackgroundImage(props.backgroundImage))

const showBackgroundPositionX = computed(
  () => !props.hideBackgroundPositionX && props.backgroundSize && !['100% 100%'].includes(props.backgroundSize),
)
const showBackgroundPositionY = computed(
  () => !props.hideBackgroundPositionY && props.backgroundSize && !['100% 100%'].includes(props.backgroundSize),
)
const showBackgroundRepeat = computed(
  () => !props.hideBackgroundRepeat && props.backgroundSize && !['100% 100%'].includes(props.backgroundSize),
)

const { t } = useI18n()

const sizeOptions = computed(() => {
  const options: { label: string; value: BackgroundSize }[] = [
    { label: t('canvasUI.components.CBackgroundImageThemeOptions.sizeOptions.auto'), value: 'auto' },
    { label: t('canvasUI.components.CBackgroundImageThemeOptions.sizeOptions.contain'), value: 'contain' },
    { label: t('canvasUI.components.CBackgroundImageThemeOptions.sizeOptions.cover'), value: 'cover' },
    { label: t('canvasUI.components.CBackgroundImageThemeOptions.sizeOptions.stretch'), value: '100% 100%' },
  ]

  if (props.backgroundSizeOptions?.length) {
    return options.filter((option) => props.backgroundSizeOptions?.includes(option.value))
  }

  return options
})

const horizontalPositionOptions = [
  { label: t('canvasUI.components.CBackgroundImageThemeOptions.positionOptions.left'), value: 'left' },
  { label: t('canvasUI.components.CBackgroundImageThemeOptions.positionOptions.center'), value: 'center' },
  { label: t('canvasUI.components.CBackgroundImageThemeOptions.positionOptions.right'), value: 'right' },
]

const verticalPositionOptions = [
  { label: t('canvasUI.components.CBackgroundImageThemeOptions.positionOptions.top'), value: 'top' },
  { label: t('canvasUI.components.CBackgroundImageThemeOptions.positionOptions.center'), value: 'center' },
  { label: t('canvasUI.components.CBackgroundImageThemeOptions.positionOptions.bottom'), value: 'bottom' },
]

const repeatOptions = [
  { label: t('canvasUI.components.CBackgroundImageThemeOptions.repeatOptions.noRepeat'), value: 'no-repeat' },
  { label: t('canvasUI.components.CBackgroundImageThemeOptions.repeatOptions.repeat'), value: 'repeat' },
  { label: t('canvasUI.components.CBackgroundImageThemeOptions.repeatOptions.repeatX'), value: 'repeat-x' },
  { label: t('canvasUI.components.CBackgroundImageThemeOptions.repeatOptions.repeatY'), value: 'repeat-y' },
]

const attachmentOptions = [
  { label: t('canvasUI.components.CBackgroundImageThemeOptions.attachmentOptions.local'), value: 'local' },
  { label: t('canvasUI.components.CBackgroundImageThemeOptions.attachmentOptions.scroll'), value: 'scroll' },
  { label: t('canvasUI.components.CBackgroundImageThemeOptions.attachmentOptions.fixed'), value: 'fixed' },
]

function updateBackgroundProperty(property: string, value: any) {
  emit('bg-property-change', property, value)
}
</script>
